// @flow
import React, { type Node } from 'react';
import { FastField, Field, useFormikContext } from 'formik';
import * as yup from 'yup';

import { DateInput } from './formik-input/DateInput.jsx';
import { Checkbox } from './formik-input/Checkbox.jsx';
import { CreditCardExpiryInput } from './formik-input/CreditCardExpiryInput.jsx';
import { DatePickerInput } from './formik-input/DatePickerInput.jsx';
import { Hidden } from './formik-input/Hidden';
import { Input } from './formik-input/Input.jsx';
import { Radio } from './formik-input/Radio';
import { Select } from './formik-input/Select.jsx';
import { Textarea } from './formik-input/Textarea.jsx';
import { getValidateFieldFunction } from './inputsUtils';

export type FormikInputProps = {
  name: string,
  label?: string | Node,
  labelAdditionalInfo?: string | Node,
  customId?: string,
  type:
    | 'birthdate'
    | 'checkbox'
    | 'creditCardExpiry'
    | 'datepicker'
    | 'email'
    | 'hidden'
    | 'number'
    | 'password'
    | 'radio'
    | 'select'
    | 'tel'
    | 'text'
    | 'textarea',
  validate?: () => boolean,
  dataTestId?: string,
  optimized?: boolean,
  hideErrorMessage?: boolean,
  isDisabled?: boolean,
  isRequired?: boolean,
  isValidate?: boolean,
  iconColor?: string,
};

const getComponentByType = type => {
  switch (type) {
    case 'birthdate':
      return DateInput;
    case 'checkbox':
      return Checkbox;
    case 'creditCardExpiry':
      return CreditCardExpiryInput;
    case 'datepicker':
      return DatePickerInput;
    case 'hidden':
      return Hidden;
    case 'radio':
      return Radio;
    case 'select':
      return Select;
    case 'textarea':
      return Textarea;
    default:
      return Input;
  }
};

const requiredValidationRule = getValidateFieldFunction(
  yup
    .mixed()
    .transform(value => !!value)
    .oneOf([true])
    .required(),
);
const getValidateFunction = (validate, isRequired, context, isValidate = true) => {
  if (validate) {
    return getValidateFieldFunction(validate, context);
  }

  if (isValidate) {
    if (isRequired) {
      return requiredValidationRule;
    }
  }

  return undefined;
};

export const FormikInput = ({
  hideErrorMessage = false,
  isRequired = false,
  optimized = false,
  type,
  customId,
  validate,
  labelAdditionalInfo,
  isValidate = true,
  ...otherProps
}: FormikInputProps) => {
  const context = useFormikContext();
  const FormikField = optimized ? FastField : Field;

  return (
    <FormikField
      component={getComponentByType(type)}
      hideErrorMessage={hideErrorMessage}
      isRequired={isRequired}
      type={type}
      customId={customId}
      labelAdditionalInfo={labelAdditionalInfo}
      validate={getValidateFunction(validate, isRequired, context, isValidate)}
      // $FlowFixMe
      {...otherProps}
    />
  );
};
